import React, { useState, useRef, useEffect } from "react";
import { Button, Container } from "react-bootstrap";
import Masonry, { ResponsiveMasonry } from "react-responsive-masonry";
import { gsap } from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";

//  Components
import ProjectCard from "../../../components/ProjectCard";
import Spinner from "../../../components/Spinner";

//  Hooks
import { useProjectsFetch } from "../../../hooks/useProjectFetch";

//  Styles
import "./Projects.scss";

const Projects = () => {
  gsap.registerPlugin(ScrollTrigger);
  const { state, loading, error, setIsLoadingMore, setLoading } =
    useProjectsFetch();
  const [filterKey, setFilterKey] = useState("*");

  //  Props references
  const textRefs = useRef([]);
  const projectsRef = useRef(null);
  textRefs.current = [];
  projectsRef.current = null;

  function calcDelay(index) {
    if (index === 0) return index;
    return index - index * 0.8;
  }

  useEffect(() => {
    textRefs.current.forEach((el, index) => {
      gsap.fromTo(
        el,
        { autoAlpha: 0, y: 50 },
        {
          autoAlpha: 1,
          y: 0,
          duration: 1,
          delay: calcDelay(index),
          ease: "power4.out",
          scrollTrigger: {
            id: "projetos",
            trigger: el,
            start: "top center+=100",
          },
        }
      );
    });

    gsap.fromTo(
      projectsRef.current,
      { autoAlpha: 0, y: 50 },
      {
        autoAlpha: 1,
        y: 0,
        duration: 1,
        ease: "power4.out",
        scrollTrigger: {
          id: "projetos",
          trigger: projectsRef.current,
          start: "top center",
        },
      }
    );
  }, []);

  const addToTextRefs = (el) => {
    if (el && !textRefs.current.includes(el)) {
      textRefs.current.push(el);
    }
  };

  //  Masonry Config
  const breakpointsColumnsObj = {
    default: 3,
    1200: 3,
    996: 3,
    768: 2,
    576: 1,
  };

  const handleLoadMore = () => {
    setLoading(true);
    setTimeout(() => {
      setIsLoadingMore(true);
    }, 2000);
  };

  return (
    <div id="projetos" className="block">
      <Container>
        <h2 className="text-center title" ref={addToTextRefs}>
          Recent Projects 📁
        </h2>
        <h4 className="subtitle text-center mb-4" ref={addToTextRefs}>
          Here are some projects that I have developed recently. Want to see
          more?{" "}
          <a href="/contacto" className="email-me">
            Please contact me
          </a>
          .
        </h4>
        <div className="button-group" ref={addToTextRefs}>
          <button
            type="button"
            className={filterKey === "*" ? "active" : ""}
            data-filter="*"
            onClick={() => setFilterKey("*")}
          >
            All
          </button>
          <button
            type="button"
            className={filterKey === "website" ? "active" : ""}
            data-filter="website"
            onClick={() => setFilterKey("website")}
          >
            Websites
          </button>
          <button
            type="button"
            className={filterKey === "email" ? "active" : ""}
            data-filter="email"
            onClick={() => setFilterKey("email")}
          >
            Emails
          </button>
          <button
            type="button"
            className={filterKey === "app" ? "active" : ""}
            data-filter="app"
            onClick={() => setFilterKey("app")}
          >
            Applications
          </button>
          <button
            type="button"
            className={filterKey === "jogo" ? "active" : ""}
            data-filter="jogo"
            onClick={() => setFilterKey("jogo")}
          >
            Games
          </button>
        </div>
        <div ref={projectsRef}>
          {error && !loading ? (
            "⚠️ There was an error while loading the projects."
          ) : (
            <>
              <ResponsiveMasonry
                columnsCountBreakPoints={breakpointsColumnsObj}
              >
                <Masonry gutter={30}>
                  {state.results.map((project) => {
                    if (filterKey === "*") {
                      return (
                        <ProjectCard project={project} key={project.name} />
                      );
                    } else {
                      let classes = project.classes.split(" ");
                      if (classes.includes(filterKey)) {
                        return (
                          <ProjectCard project={project} key={project.name} />
                        );
                      }
                      return <></>;
                    }
                  })}
                </Masonry>
              </ResponsiveMasonry>
              <div className="text-center my-5" ref={addToTextRefs}>
                {loading && <Spinner color="red" />}
                {state.page < state.total_pages && !loading && (
                  <Button variant="primary" onClick={handleLoadMore}>
                    Load More
                  </Button>
                )}
              </div>
            </>
          )}
        </div>
      </Container>
    </div>
  );
};

export default Projects;
